import { useQuery } from "@tanstack/react-query";
import { useParams, useLocation } from "wouter";
import { MetricCard } from "@/components/metric-card";
import { DataTable } from "@/components/data-table";
import { DashboardSkeleton } from "@/components/loading-skeleton";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import {
  DollarSign,
  Users,
  FileText,
  ShoppingCart,
  ArrowLeft,
  AlertCircle,
} from "lucide-react";
import type { CampaignMetrics, SalesReportData } from "@re/shared";

export default function Campaigns() {
  const params = useParams<{ id?: string }>();
  const [, navigate] = useLocation();
  const campaignId = params.id;

  const { data, isLoading, error } = useQuery<SalesReportData>({
    queryKey: ["/api/report"],
  });

  if (isLoading) {
    return (
      <div className="p-6">
        <DashboardSkeleton />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <AlertCircle className="h-12 w-12 text-destructive mb-4" />
            <h3 className="text-lg font-semibold mb-2">Failed to load data</h3>
            <p className="text-muted-foreground text-center">
              Please try again later.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!data) {
    return null;
  }

  const allCampaigns = data.products.flatMap((p) => p.campaigns);

  if (campaignId) {
    const campaign = allCampaigns.find((c) => c.campaignId === campaignId);

    if (!campaign) {
      return (
        <div className="p-6">
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-12">
              <AlertCircle className="h-12 w-12 text-muted-foreground mb-4" />
              <h3 className="text-lg font-semibold mb-2">Campaign not found</h3>
              <Button onClick={() => navigate("/campaigns")} data-testid="button-back-campaigns">
                Back to Campaigns
              </Button>
            </CardContent>
          </Card>
        </div>
      );
    }

    return <CampaignDetail campaign={campaign} />;
  }

  const campaignColumns = [
    {
      key: "campaignName",
      header: "Engineer",
      render: (item: CampaignMetrics) => (
        <span className="font-medium">{item.campaignName}</span>
      ),
    },
    {
      key: "source",
      header: "Category",
      render: (item: CampaignMetrics) => (
        <Badge variant="outline">
          {item.source}
        </Badge>
      ),
    },
    {
      key: "conversions",
      header: "Opportunities",
      sortable: true,
      align: "right" as const,
      render: (item: CampaignMetrics) => item.conversions.toLocaleString(),
    },
    {
      key: "quotes",
      header: "Active",
      sortable: true,
      align: "right" as const,
      render: (item: CampaignMetrics) => (
        <div className="flex items-center justify-end gap-2">
          <span>{item.quotes.toLocaleString()}</span>
          <Badge variant="secondary" className="text-xs">
            {item.quoteRate.toFixed(0)}%
          </Badge>
        </div>
      ),
    },
    {
      key: "sales",
      header: "Completed",
      sortable: true,
      align: "right" as const,
      render: (item: CampaignMetrics) => (
        <div className="flex items-center justify-end gap-2">
          <span>{item.sales.toLocaleString()}</span>
          <Badge variant="secondary" className="text-xs">
            {item.saleRate.toFixed(0)}%
          </Badge>
        </div>
      ),
    },
    {
      key: "revenue",
      header: "Quoted Value",
      sortable: true,
      align: "right" as const,
      render: (item: CampaignMetrics) => `£${item.revenue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
    },
  ];

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">All Campaigns</h1>
        <p className="text-muted-foreground">
          Marketing campaign performance across all products
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Campaign Performance</CardTitle>
          <CardDescription>
            Click on a campaign to view detailed metrics
          </CardDescription>
        </CardHeader>
        <CardContent>
          <DataTable
            data={allCampaigns}
            columns={campaignColumns}
            onRowClick={(item) => navigate(`/campaigns/${item.campaignId}`)}
            showDrilldown
          />
        </CardContent>
      </Card>
    </div>
  );
}

function CampaignDetail({ campaign }: { campaign: CampaignMetrics }) {
  const [, navigate] = useLocation();

  return (
    <div className="p-6 space-y-6">
      <div className="space-y-4">
        <Breadcrumb>
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink href="/campaigns">Campaigns</BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbPage>{campaign.campaignName}</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>

        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate("/campaigns")}
            data-testid="button-back"
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold tracking-tight">
                {campaign.campaignName}
              </h1>
              <Badge variant="outline">
                {campaign.source}
              </Badge>
            </div>
            <p className="text-muted-foreground">
              Detailed performance metrics for this campaign
            </p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard
          title="Opportunities"
          value={campaign.conversions}
          subtitle="Total assigned jobs"
          icon={<Users className="h-4 w-4" />}
        />
        <MetricCard
          title="Active Jobs"
          value={campaign.quotes}
          subtitle={`${campaign.quoteRate.toFixed(0)}% of total`}
          icon={<FileText className="h-4 w-4" />}
        />
        <MetricCard
          title="Completed"
          value={campaign.sales}
          subtitle={`${campaign.saleRate.toFixed(0)}% completion rate`}
          icon={<ShoppingCart className="h-4 w-4" />}
        />
        <MetricCard
          title="Quoted Value"
          value={`£${campaign.revenue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
          icon={<DollarSign className="h-4 w-4" />}
        />
      </div>
    </div>
  );
}
